import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Waves, Sun, Heart, Coffee, Sparkles, Calendar, MapPin, Clock, CheckCircle2, Users, X, User, Mail, Phone, Lock, Info } from 'lucide-react';
import { openTranzilaPayment } from '../utils/tranzila';
import { sendWomensSupRegistrationToMake } from '../utils/makeWebhook';
import { UtmCapture } from '../components/UtmCapture';
import { getUtmParams } from '../utils/utmTracking';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
// Open registration dates — keep in sync with the hero / details sections
const EVENT_DATES = [{
  value: '31.07.2026',
  label: 'שישי, 31.07.2026'
}, {
  value: '04.09.2026',
  label: 'שישי, 04.09.2026'
}, {
  value: '08.10.2026',
  label: 'שישי, 08.10.2026'
}];
export function WomensSUPMorningPage() {
  const PRICE = 480;
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  // ============================================
  // TEST MODE - DELETE THIS BLOCK TO REMOVE
  // Activate via URL: ?test_mode=womenssup2026
  // ============================================
  const [isTestMode, setIsTestMode] = useState(false);
  // ============================================
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    selectedDate: '',
    dateOfBirth: '',
    noMedicalLimitations: false,
    hasExperience: '',
    notes: ''
  });
  const paymentIframeRef = useRef<HTMLIFrameElement>(null);
  // Detect payment success — runs in both top window AND iframe context.
  // If we're inside an iframe at /womens-sup-morning?paid=1, send the
  // registration to Make.com (ONLY happens after a successful Tranzila payment)
  // and then notify the parent window so it can show the success state.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // Detect success from EITHER our own marker OR Tranzila's response param.
    // Tranzila replaces our query string with its own params on redirect.
    const isPaid = urlParams.get('paid') === '1' || urlParams.get('Response') === '000';
    if (!isPaid) return;
    console.log('💳 Payment success detected on page load', {
      paid: urlParams.get('paid'),
      Response: urlParams.get('Response'),
      ConfirmationCode: urlParams.get('ConfirmationCode'),
      isInIframe: window.parent !== window
    });
    const sendWebhookAndNotify = async () => {
      // Read the registration data saved at form submission
      let registration: any = null;
      try {
        const stored = localStorage.getItem('womensSupMorningRegistration');
        if (stored) registration = JSON.parse(stored);
      } catch (err) {
        console.warn('Could not read registration from localStorage', err);
      }
      // Send to Make.com — only after a confirmed successful payment
      if (registration) {
        // Prefer UTM params stored with the registration (captured at submit
        // time, more reliable in Meta in-app browsers). Fall back to the
        // global UTM store if anything is missing.
        const fallbackUtm = getUtmParams();
        const utm = {
          utm_source: registration.utm_source || fallbackUtm.utm_source,
          utm_medium: registration.utm_medium || fallbackUtm.utm_medium,
          utm_campaign: registration.utm_campaign || fallbackUtm.utm_campaign,
          utm_content: registration.utm_content || fallbackUtm.utm_content,
          utm_term: registration.utm_term || fallbackUtm.utm_term,
          utm_id: registration.utm_id || fallbackUtm.utm_id,
          fbclid: registration.fbclid || fallbackUtm.fbclid
        };
        try {
          await sendWomensSupRegistrationToMake({
            type: 'Womens SUP Morning Registration',
            source: 'MagicPatterns',
            paid: true,
            event: registration.event || 'בוקר נשי - הכוח שבמים',
            eventDate: registration.eventDate || '31.07.2026',
            fullName: registration.fullName || '',
            email: registration.email || '',
            phone: registration.phone || '',
            dateOfBirth: registration.dateOfBirth || '',
            noMedicalLimitations: !!registration.noMedicalLimitations,
            hasExperience: registration.hasExperience || '',
            notes: registration.notes || '',
            price: registration.price ?? '',
            submittedAt: registration.submittedAt || new Date().toISOString(),
            ...utm
          });
          // NOTE: wm_purchase pixel event is fired in the TOP WINDOW
          // (see message listener below), NOT here in the iframe — the
          // iframe is too short-lived for fbevents.js to finish loading
          // and dispatch the network call before the modal closes.
          // Clear stored data after a successful send
          localStorage.removeItem('womensSupMorningRegistration');
        } catch (err) {
          console.error('Webhook send failed', err);
        }
      }
      if (window.parent !== window) {
        // Inside the Tranzila iframe — tell the parent to show the success UI.
        // Include submittedAt so the parent can dedup wm_purchase even if it
        // already cleared localStorage.
        try {
          window.parent.postMessage({
            type: 'womens-sup-payment-success',
            submittedAt: registration?.submittedAt
          }, window.location.origin);
        } catch (err) {
          console.warn('Could not postMessage to parent', err);
        }
      } else {
        // Top-level visit with paid=1 (fallback). Show success directly.
        setPaymentSuccess(true);
        setIsPaymentOpen(true);
        window.history.replaceState({}, '', window.location.pathname);
      }
    };
    sendWebhookAndNotify();
  }, []);
  // Top window: listen for the success message from the iframe.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'womens-sup-payment-success') {
        setPaymentSuccess(true);
        setIsSubmitting(false);
        // Meta Pixel — fire wm_purchase custom event HERE in the top window.
        // The top window has had the full app loaded for the entire session,
        // so fbevents.js is fully initialized and the call will dispatch
        // a proper /tr/ network request with all parameters.
        // Dedup on submittedAt to avoid double-firing on remounts.
        try {
          const stored = localStorage.getItem('womensSupMorningRegistration');
          const reg = stored ? JSON.parse(stored) : null;
          const submittedAt = reg?.submittedAt || event.data?.submittedAt || '';
          const dedupKey = `wm_purchase_fired_${submittedAt}`;
          const alreadyFired = submittedAt && localStorage.getItem(dedupKey);
          if (!alreadyFired && typeof window !== 'undefined' && (window as any).fbq) {
            ;
            (window as any).fbq('trackCustom', 'wm_purchase', {
              content_name: 'בוקר נשי - הכוח שבמים',
              content_category: 'Womens SUP Morning',
              value: reg?.price ?? PRICE,
              currency: 'ILS',
              utm_source: reg?.utm_source,
              utm_medium: reg?.utm_medium,
              utm_campaign: reg?.utm_campaign,
              utm_content: reg?.utm_content,
              utm_term: reg?.utm_term,
              utm_id: reg?.utm_id,
              fbclid: reg?.fbclid
            });
            if (submittedAt) localStorage.setItem(dedupKey, '1');
          }
        } catch (err) {
          console.warn('Could not fire wm_purchase pixel event', err);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  // ============================================
  // TEST MODE - DELETE THIS BLOCK TO REMOVE
  // ============================================
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testModeParam = urlParams.get('test_mode');
    if (testModeParam === 'womenssup2026') {
      setIsTestMode(true);
      localStorage.setItem('womens_test_mode_active', 'true');
      console.log('🧪 WOMENS SUP TEST MODE ACTIVATED - Charge will be ₪1');
    } else {
      const stored = localStorage.getItem('womens_test_mode_active');
      if (stored === 'true') {
        setIsTestMode(true);
        console.log('🧪 WOMENS SUP TEST MODE ACTIVE (from localStorage)');
      }
    }
  }, []);
  // ============================================
  const openForm = () => {
    setPaymentSuccess(false);
    setIsFormOpen(true);
    // Meta Pixel — track form opened (AddToCart equivalent)
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart', {
        content_name: 'בוקר נשי - הכוח שבמים',
        content_category: 'Womens SUP Morning',
        value: PRICE,
        currency: 'ILS'
      });
    }
  };
  const closeForm = () => {
    if (!isSubmitting) setIsFormOpen(false);
  };
  const closePayment = () => {
    setIsPaymentOpen(false);
    setIsSubmitting(false);
    setPaymentSuccess(false);
  };
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.selectedDate || !formData.dateOfBirth || !formData.noMedicalLimitations || !formData.hasExperience) return;
    setIsSubmitting(true);
    // ============================================
    // TEST MODE - DELETE THIS BLOCK TO REMOVE
    // ============================================
    const chargeSum = isTestMode ? 1 : PRICE;
    // ============================================
    // Capture UTM params NOW at form submission time so they survive any
    // localStorage clearing that might happen in Meta in-app browsers.
    const utmParams = getUtmParams();
    console.log('📊 Capturing UTM params at form submission:', utmParams);
    // Persist registration for later record keeping / thank you page
    try {
      localStorage.setItem('womensSupMorningRegistration', JSON.stringify({
        ...formData,
        event: 'בוקר נשי - הכוח שבמים',
        eventDate: formData.selectedDate,
        price: chargeSum,
        submittedAt: new Date().toISOString(),
        // UTM params stored alongside registration
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_content: utmParams.utm_content,
        utm_term: utmParams.utm_term,
        utm_id: utmParams.utm_id,
        fbclid: utmParams.fbclid
      }));
    } catch (err) {
      console.warn('Could not persist registration to localStorage', err);
    }
    // Track Meta Pixel event if available
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'InitiateCheckout', {
        content_name: 'בוקר נשי - הכוח שבמים',
        content_category: 'Womens SUP Morning',
        value: chargeSum,
        currency: 'ILS'
      });
    }
    // Close form modal and open the payment modal with embedded iframe
    setIsFormOpen(false);
    setIsPaymentOpen(true);
    // Defer to ensure the iframe (name="tranzila") is in the DOM before submitting the form
    setTimeout(() => {
      openTranzilaPayment({
        sum: chargeSum,
        contact: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        pdesc: `בוקר נשי - הכוח שבמים (${formData.selectedDate})`,
        success_url: `${window.location.origin}/womens-sup-morning?paid=1`,
        fail_url: window.location.href
      });
    }, 150);
  };
  // If we're loaded inside the Tranzila iframe at /womens-sup-morning?paid=1,
  // skip rendering the full landing page. The iframe is short-lived and only
  // needs to send the webhook + postMessage to the parent. Showing the full
  // page would briefly flash all the marketing content inside the modal.
  const isPaidIframe = typeof window !== 'undefined' && window.parent !== window && (new URLSearchParams(window.location.search).get('paid') === '1' || new URLSearchParams(window.location.search).get('Response') === '000');
  if (isPaidIframe) {
    return <div dir="rtl" className="font-['Heebo'] min-h-screen bg-rose-50 flex items-center justify-center text-stone-700" data-id="element-2425">
        <div className="text-center" data-id="element-2426">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full border-4 border-rose-200 border-t-rose-500 animate-spin" data-id="element-2427" />
          <p className="text-base font-medium" data-id="element-2428">מאשרת את ההרשמה…</p>
        </div>
      </div>;
  }
  return <div dir="rtl" className="font-['Heebo'] bg-rose-50 text-stone-800 min-h-screen overflow-x-hidden" data-id="element-2429">
      {/* Silent UTM capture for Meta Ads attribution */}
      <UtmCapture data-id="element-2430" />

      {/* ============================================ */}
      {/* TEST MODE INDICATOR - DELETE THIS TO REMOVE */}
      {/* ============================================ */}
      {isTestMode && <div className="fixed top-4 right-4 z-[80] bg-yellow-400 text-stone-900 px-4 py-2 rounded-lg shadow-lg font-bold text-sm flex items-center gap-2" data-id="element-2431">
          <Sparkles className="w-4 h-4" data-id="element-2432" />
          TEST MODE - ₪1
        </div>}
      {/* ============================================ */}

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden py-28 md:py-24" data-id="element-2433">
        <div className="absolute inset-0 z-0" data-id="element-2434">
          <img src="https://cdn.magicpatterns.com/uploads/7UQXFa8QVzGYPRJjgEmh4j/c61b37f1-fb9e-4cd8-b43e-2e77a03ab830_(1).jpg" alt="בוקר נשי על הסאפ בכנרת" className="w-full h-full object-cover" data-id="element-2435" />
          <div className="absolute inset-0 bg-gradient-to-b from-rose-900/30 via-rose-800/40 to-stone-900/70" data-id="element-2436"></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-16" data-id="element-2437">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} data-id="element-2438">
            {/* Sold-out dates — August now sold out too */}
            <div className="inline-flex flex-col items-center justify-center gap-3 py-4 px-6 sm:px-8 rounded-3xl bg-rose-500/30 text-white border-2 border-rose-300/60 mb-4 backdrop-blur-md shadow-2xl shadow-rose-900/40" data-id="element-2439">
              <span className="text-xs sm:text-sm uppercase tracking-[0.2em] text-rose-200/80 font-semibold" data-id="element-2440">
                המפגשים הקרובים אזלו
              </span>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3" data-id="element-2441">
                <span className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full bg-stone-900/40 text-stone-200 border border-stone-500/40 text-base sm:text-lg font-semibold backdrop-blur-sm relative overflow-hidden" data-id="element-2442">
                  <Calendar className="w-4 h-4" data-id="element-2443" />
                  <span className="line-through opacity-70" data-id="element-2444">03.07.2026</span>
                  <span className="absolute inset-0 flex items-center justify-center bg-stone-900/60 font-bold text-rose-400 tracking-wider text-xs uppercase" style={{
                  transform: 'rotate(-5deg)'
                }} data-id="element-2445">
                    SOLD OUT
                  </span>
                </span>
                <span className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full bg-stone-900/40 text-stone-200 border border-stone-500/40 text-base sm:text-lg font-semibold backdrop-blur-sm relative overflow-hidden" data-id="element-2446">
                  <Calendar className="w-4 h-4" data-id="element-2447" />
                  <span className="line-through opacity-70" data-id="element-2448">07.08.2026</span>
                  <span className="absolute inset-0 flex items-center justify-center bg-stone-900/60 font-bold text-rose-400 tracking-wider text-xs uppercase" style={{
                  transform: 'rotate(-5deg)'
                }} data-id="element-2449">
                    SOLD OUT
                  </span>
                </span>
              </div>
            </div>

            {/* Open dates — registration now open */}
            <div className="inline-flex flex-col items-center justify-center gap-3 py-4 px-6 sm:px-8 rounded-3xl bg-emerald-500/25 text-white border-2 border-emerald-300/60 mb-6 backdrop-blur-md shadow-2xl shadow-emerald-900/30" data-id="element-2450">
              <span className="text-xs sm:text-sm uppercase tracking-[0.2em] text-emerald-100/90 font-semibold" data-id="element-2451">
                תאריכים חדשים — ההרשמה פתוחה!
              </span>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3" data-id="element-2452">
                <span className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full bg-white/90 text-emerald-700 border border-emerald-200 text-base sm:text-lg font-bold backdrop-blur-sm" data-id="element-2453">
                  <Calendar className="w-4 h-4" data-id="element-2454" />
                  שישי, 31.07.2026
                </span>
                <span className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full bg-white/90 text-emerald-700 border border-emerald-200 text-base sm:text-lg font-bold backdrop-blur-sm" data-id="element-2455">
                  <Calendar className="w-4 h-4" data-id="element-2456" />
                  04.09.2026
                </span>
                <span className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full bg-white/90 text-emerald-700 border border-emerald-200 text-base sm:text-lg font-bold backdrop-blur-sm" data-id="element-2457">
                  <Calendar className="w-4 h-4" data-id="element-2458" />
                  08.10.2026
                </span>
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-4 leading-tight" data-id="element-2459">
              בוקר נשי - <span className="text-rose-300" data-id="element-2460">הכוח שבמים</span>
            </h1>
            <p dir="ltr" className="text-2xl md:text-4xl text-rose-100 mb-6 font-light italic tracking-wide" data-id="element-2461">
              Come Sup With Me
            </p>
            <p className="text-xl md:text-2xl text-rose-50 mb-10 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-2462">
              בואי ככה, בדיוק כמו שאת. כל השאר – עליי.
            </p>
            <button type="button" onClick={openForm} className="group inline-flex items-center justify-center gap-2.5 bg-rose-500 hover:bg-rose-600 text-white text-base sm:text-lg font-bold py-4 px-8 sm:px-10 rounded-full transition-all duration-200 shadow-xl shadow-rose-900/40 hover:shadow-2xl hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-4 focus-visible:ring-rose-300/60" data-id="element-2463">
              <Sparkles className="w-5 h-5 shrink-0" data-id="element-2464" />
              <span data-id="element-2465">הרשמה ותשלום לתאריכים הבאים</span>
              <span className="transition-transform duration-200 group-hover:-translate-x-1" data-id="element-2466">
                ←
              </span>
            </button>
          </motion.div>
        </div>

        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2
      }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 z-10" data-id="element-2467">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center p-1" data-id="element-2468">
            <div className="w-1.5 h-1.5 bg-white rounded-full" data-id="element-2469"></div>
          </div>
        </motion.div>
      </section>

      {/* Invitation / Personal Note */}
      <section className="py-24 px-4 bg-white" data-id="element-2470">
        <div className="max-w-3xl mx-auto text-center" data-id="element-2471">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} data-id="element-2472">
            <div className="inline-flex items-center gap-2 mb-6 text-rose-500" data-id="element-2473">
              <Heart className="w-5 h-5" data-id="element-2474" />
              <span className="text-sm font-semibold tracking-wider uppercase" data-id="element-2475">
                ההזמנה שלי אלייך
              </span>
              <Heart className="w-5 h-5" data-id="element-2476" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-8 leading-tight" data-id="element-2477">
              לפעמים את צריכה רק בוקר אחד.
            </h2>
            <div className="space-y-5 text-lg md:text-xl text-stone-600 leading-relaxed" data-id="element-2478">
              <p data-id="element-2479">
                בוקר אחד שבו את מתנתקת מהיומיום, מתמסרת, מתחברת אל הטבע, ומרגישה
                את הכוח חוזר אלייך.
              </p>
              <p className="font-medium text-stone-800" data-id="element-2480">
                בלי ניסיון קודם. בלי שיפוטיות. רק את, המים וקבוצת נשים בדיוק
                כמוך.
              </p>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="mt-14 rounded-3xl overflow-hidden shadow-xl shadow-rose-200/40 border border-rose-100" data-id="element-2481">
              <img src="https://cdn.magicpatterns.com/uploads/pvDjVs7yuSgf7363qaT2WF/IMG_8179_(1).jpg" alt="קבוצת נשים בבוקר נשי בחוף מעגן" className="w-full h-auto object-cover" data-id="element-2482" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* About Adi */}
      <section className="py-24 px-4 bg-gradient-to-b from-white to-rose-50" data-id="element-2483">
        <div className="max-w-3xl mx-auto" data-id="element-2484">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="bg-white rounded-3xl shadow-xl shadow-rose-100/50 border border-rose-100 p-8 md:p-12" data-id="element-2485">
            <div className="inline-flex items-center gap-2 mb-5 text-rose-500" data-id="element-2486">
              <Heart className="w-4 h-4" data-id="element-2487" />
              <span className="text-xs font-semibold tracking-wider uppercase" data-id="element-2488">
                מי שמחכה לך שם
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6 leading-tight" data-id="element-2489">
              שלום, אני עדי
            </h2>

            <div className="space-y-4 text-stone-600 text-base md:text-lg leading-relaxed" data-id="element-2490">
              <p data-id="element-2491">
                גיליתי את עולם הסאפ בשנים האחרונות וזה שינה אותי מבחוץ ומבפנים.
                התמכרתי - ואני מזמינה גם אותך לחולל בעצמך שינוי אמיתי, כזה שיעיר
                ויעורר, ירגש ויסחוף.
              </p>
              <p data-id="element-2492">
                ההתמודדות עם הטבע, השקט והתנועה המתמדת על המים אפשרה לי להתחבר
                לקול הפנימי שלי ולגלות בי עוצמות שלא הכרתי.
              </p>
              <p className="font-semibold text-stone-800 text-lg md:text-xl pt-2" data-id="element-2493">
                מזמינה גם אותך לגלות בעצמך עולמות חדשים. 🌊
              </p>
            </div>

            <div className="mt-8 mb-2 flex items-center gap-3" data-id="element-2494">
              <div className="h-px bg-rose-200 flex-1" data-id="element-2495"></div>
              <span className="text-rose-400 text-2xl" style={{
              fontFamily: '"Brush Script MT", cursive'
            }} data-id="element-2496">
                Adi
              </span>
              <div className="h-px bg-rose-200 flex-1" data-id="element-2497"></div>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="mt-10 rounded-2xl overflow-hidden shadow-lg shadow-rose-200/40 border border-rose-100" data-id="element-2498">
              <img src="https://cdn.magicpatterns.com/uploads/o8NBipbPs9TUtEVJiiwzvh/image.png" alt="עדי על קרש סאפ בכנרת" className="w-full h-auto object-cover" data-id="element-2499" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What's in the morning */}
      <section className="py-24 px-4 bg-gradient-to-b from-rose-50 to-orange-50" data-id="element-2500">
        <div className="max-w-6xl mx-auto" data-id="element-2501">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-2502">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-2503">
              מה מחכה לך בבוקר הזה?
            </h2>
            <div className="w-24 h-1 bg-rose-400 mx-auto rounded-full" data-id="element-2504"></div>
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2505">
            {[{
            image: 'https://cdn.magicpatterns.com/uploads/hfUVkEZQekPyb43CZMktV3/GetAttachmentThumbnail_(7).jpg',
            title: 'מעגל פתיחה',
            desc: 'כוס קפה חם, היכרות עדינה והכוונה לבוקר שלנו.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/iZHHfKbDuzWiZmbvcXG53T/IMG_8165.jpg',
            title: 'סאפ בכנרת',
            desc: 'ממש מאפס - היכרות עם מבנה הסאפ והמשוט, טכניקת חתירה, וחתירה במים.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/sFwj9YB7rvdyCPXtDrtWRm/IMG_5724.jpg',
            title: 'בראנץ׳ נשי',
            desc: 'ארוחת בוקר מפנקת, יין טוב, שיחה אמיתית וסגירת מעגל.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/eom8E644yCYiU5JAxE9kj9/IMG_5754.jpg',
            title: 'נשימות ואמבטיית קרח',
            desc: 'הדרכת נשימות מעמיקה וטבילה מעצימה במי קרח מול הנוף.'
          }].map((item: any, idx) => {
            const Icon = item.icon;
            return <motion.div key={idx} variants={fadeInUp} className="bg-white p-8 rounded-3xl text-center hover:shadow-xl transition-shadow border border-rose-100 flex flex-col items-center" data-id="element-2506">
                  {item.image ? <div className="w-20 h-20 rounded-full overflow-hidden mb-5 ring-4 ring-rose-100 shadow-md" data-id="element-2507">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-2508" />
                    </div> : <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mb-5" data-id="element-2509">
                      <Icon className="w-8 h-8 text-rose-500" data-id="element-2510" />
                    </div>}
                  <h3 className="text-xl font-bold mb-3 text-stone-800" data-id="element-2511">
                    {item.title}
                  </h3>
                  <p className="text-stone-600 leading-relaxed" data-id="element-2512">{item.desc}</p>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* Ice Bath & Breathing Section */}
      <section className="py-24 px-4 bg-white overflow-hidden" data-id="element-2513">
        <div className="max-w-6xl mx-auto" data-id="element-2514">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2515">
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} data-id="element-2516">
              <div className="inline-flex items-center gap-2 mb-4 text-rose-500" data-id="element-2517">
                <Sparkles className="w-5 h-5" data-id="element-2518" />
                <span className="text-sm font-semibold tracking-wider uppercase" data-id="element-2519">
                  העצמה דרך הגוף
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6 leading-tight" data-id="element-2520">
                הכוח שבנשימה ובקור
              </h2>

              <div className="space-y-6 text-lg text-stone-600 leading-relaxed" data-id="element-2521">
                <p data-id="element-2522">
                  <strong className="text-stone-800" data-id="element-2523">
                    חשיבות מערכת הנשימה:
                  </strong>{' '}
                  הנשימה היא העוגן שלנו. הדרכת הנשימות שנחווה יחד תלמד אותנו איך
                  להשתמש בנשימה כדי לווסת את מערכת העצבים, להרגיע את הגוף
                  ולהחזיר את השליטה לידיים שלנו.
                </p>
                <p data-id="element-2524">
                  <strong className="text-stone-800" data-id="element-2525">טבילה במי קרח:</strong>{' '}
                  מעבר ליתרונות הפיזיים המוכחים כמו חיזוק המערכת החיסונית, הפחתת
                  דלקתיות ושיפור זרימת הדם — הטבילה בקרח היא אימון מנטלי טהור.
                  היא מפגישה אותנו עם קושי קיצוני ומלמדת אותנו שאנחנו מסוגלות
                  להכיל אותו.
                </p>
                <div className="bg-rose-50 p-6 rounded-2xl border border-rose-100 mt-4" data-id="element-2526">
                  <p className="text-stone-800 font-medium italic" data-id="element-2527">
                    "ההתמודדות עם החתירה בכנרת והיכולת לנשום בתוך הקור, לצאת
                    ממצב של סטרס ולהרפות אל תוך הקושי — היא בדיוק אותה היכולת
                    שתשרת אותנו מול אתגרי היום-יום. כשאנחנו מתמודדות עם אתגר חדש
                    ומנצחות את הקרח - אנחנו מגלות את העוצמה האמיתית שלנו, וכוחות
                    שקיימים בנו ולא הכרנו."
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="grid grid-cols-2 gap-4" data-id="element-2528">
              <div className="space-y-4 mt-8" data-id="element-2529">
                <div className="rounded-3xl overflow-hidden shadow-lg aspect-[4/5]" data-id="element-2530">
                  <video src="https://cdn.magicpatterns.com/uploads/o4YnUbrq6uHfsVbqHW7rax/IMG_5753.MOV" autoPlay muted loop playsInline className="w-full h-full object-cover" data-id="element-2531" />
                </div>
                <div className="rounded-3xl overflow-hidden shadow-lg aspect-square" data-id="element-2532">
                  <img src="https://cdn.magicpatterns.com/uploads/sFwj9YB7rvdyCPXtDrtWRm/IMG_5724.jpg" alt="קבוצת נשים בבוקר נשי" className="w-full h-full object-cover" data-id="element-2533" />
                </div>
              </div>
              <div className="space-y-4" data-id="element-2534">
                <div className="rounded-3xl overflow-hidden shadow-lg aspect-square" data-id="element-2535">
                  <img src="https://cdn.magicpatterns.com/uploads/eom8E644yCYiU5JAxE9kj9/IMG_5754.jpg" alt="אישה באמבטיית קרח" className="w-full h-full object-cover" data-id="element-2536" />
                </div>
                <div className="rounded-3xl overflow-hidden shadow-lg aspect-[4/5]" data-id="element-2537">
                  <video src="https://cdn.magicpatterns.com/uploads/9XaBZJLpMsWrm3PxooNfNK/IMG_5728.MOV" autoPlay muted loop playsInline className="w-full h-full object-cover" data-id="element-2538" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* For Who */}
      <section className="py-24 px-4 bg-white" data-id="element-2539">
        <div className="max-w-4xl mx-auto" data-id="element-2540">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-2541">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-2542">
              הבוקר הזה הוא בשבילך אם...
            </h2>
            <div className="w-24 h-1 bg-rose-400 mx-auto rounded-full" data-id="element-2543"></div>
          </motion.div>

          <motion.ul variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="space-y-4 max-w-2xl mx-auto" data-id="element-2544">
            {['את מרגישה שמגיע לך בוקר אחד רק בשבילך', 'תמיד רצית לנסות סאפ אבל לא ידעת איפה להתחיל', 'את מתגעגעת למפגש נשי אמיתי', 'את אוהבת מים, שמש ושקט', 'בא לך להיות במרחב נטול שיפוטיות - מהסביבה וגם משלך כלפי עצמך', 'את רוצה להשיב לעצמך את הביטחון בגוף שלך, ולגלות שיש בך עוצמות ויכולות להתמודד'].map((item, idx) => <motion.li key={idx} variants={fadeInUp} className="flex items-start gap-4 bg-rose-50/50 p-5 rounded-2xl border border-rose-100" data-id="element-2545">
                <div className="bg-rose-400 rounded-full p-1 shrink-0 mt-1" data-id="element-2546">
                  <CheckCircle2 className="w-4 h-4 text-white" data-id="element-2547" />
                </div>
                <span className="text-lg text-stone-700 font-medium leading-relaxed" data-id="element-2548">
                  {item}
                </span>
              </motion.li>)}
          </motion.ul>
        </div>
      </section>

      {/* Details */}
      <section className="py-24 px-4 bg-gradient-to-b from-orange-50 to-rose-100" data-id="element-2549">
        <div className="max-w-5xl mx-auto" data-id="element-2550">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-2551">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-2552">
              לוז הבוקר
            </h2>
            <div className="w-24 h-1 bg-rose-400 mx-auto rounded-full mb-6" data-id="element-2553"></div>
            <div className="inline-flex flex-wrap items-center justify-center gap-2 text-sm" data-id="element-2554">
              <span className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-rose-200 text-stone-700 px-4 py-2 rounded-full shadow-sm" data-id="element-2555">
                <MapPin className="w-4 h-4 text-rose-500" data-id="element-2556" />
                <span className="font-medium" data-id="element-2557">חוף מעגן, כנרת</span>
              </span>
              <span className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-rose-200 text-stone-700 px-4 py-2 rounded-full shadow-sm" data-id="element-2558">
                <Clock className="w-4 h-4 text-rose-500" data-id="element-2559" />
                <span className="font-medium" data-id="element-2560">09:00 - 13:00</span>
              </span>
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-sm" data-id="element-2561">
              <span className="text-stone-600 font-semibold ml-1" data-id="element-2562">
                תאריכים פתוחים להרשמה:
              </span>
              {['שישי, 31.07.2026', '04.09.2026', '08.10.2026'].map(d => <span key={d} className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-2 rounded-full shadow-sm font-bold" data-id="element-2563">
                  <Calendar className="w-4 h-4 text-emerald-500" data-id="element-2564" />
                  {d}
                </span>)}
            </div>
          </motion.div>

          <div className="max-w-3xl mx-auto mb-16" data-id="element-2565">
            <div className="relative" data-id="element-2566">
              {/* Vertical timeline connector */}
              <div className="absolute right-6 md:right-8 top-3 bottom-3 w-px bg-gradient-to-b from-rose-300 via-rose-300 to-orange-300" data-id="element-2567"></div>

              <ul className="space-y-6" data-id="element-2568">
                {[{
                time: '09:00',
                title: 'מעגל פתיחה',
                desc: 'מפגש ראשון על החוף, היכרות וכוונה לבוקר'
              }, {
                time: '10:00',
                title: 'סאפ בכנרת',
                desc: 'הדרכה מקצועית למתחילות וגלישה רגועה על המים'
              }, {
                time: '11:30',
                title: 'בראנץ׳ נשי',
                desc: 'ארוחה מפנקת, יין טוב, שיחה אמיתית וסגירת מעגל'
              }, {
                time: '12:30',
                title: 'הדרכת נשימות ואמבטיית קרח',
                desc: 'חיבור למערכת הנשימה, ויסות סטרס וטבילה מעצימה במי קרח'
              }].map((item, idx) => {
                return <motion.li key={idx} initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: idx * 0.1,
                  duration: 0.5
                }} className="relative flex items-start gap-4 md:gap-6" data-id="element-2569">
                      {/* Time marker */}
                      <div className="relative z-10 shrink-0" data-id="element-2570">
                        <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white flex flex-col items-center justify-center shadow-md ring-4 ring-rose-100 border border-rose-200" data-id="element-2571">
                          <span className="text-rose-600 font-bold text-sm md:text-base leading-none" data-id="element-2572">
                            {item.time}
                          </span>
                        </div>
                      </div>

                      {/* Content card */}
                      <div className="flex-1 bg-white rounded-2xl p-5 md:p-6 border border-rose-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-2573">
                        <h3 className="text-lg md:text-xl font-bold text-stone-900 mb-2" data-id="element-2574">
                          {item.title}
                        </h3>
                        <p className="text-stone-600 leading-relaxed" data-id="element-2575">
                          {item.desc}
                        </p>
                      </div>
                    </motion.li>;
              })}
              </ul>
            </div>
          </div>

          {/* Price Card */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto" data-id="element-2576">
            <div className="relative rounded-3xl overflow-hidden shadow-xl shadow-rose-300/30 border border-rose-200 bg-gradient-to-br from-rose-100 via-rose-50 to-orange-50" data-id="element-2577">
              <div className="relative z-10 p-8 md:p-12 text-center" data-id="element-2578">
                <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm border border-rose-200 px-4 py-1.5 rounded-full mb-6 text-rose-600" data-id="element-2579">
                  <Users className="w-4 h-4" data-id="element-2580" />
                  <span className="text-sm font-medium" data-id="element-2581">מקומות מוגבלים</span>
                </div>

                <div className="text-rose-500 text-lg mb-3 font-medium" data-id="element-2582">
                  השקעה בבוקר שלך
                </div>
                <div className="flex items-baseline justify-center gap-2 mb-2" data-id="element-2583">
                  <span className="text-6xl md:text-7xl font-extrabold text-rose-700" data-id="element-2584">
                    ₪480
                  </span>
                </div>
                <p className="text-stone-600 text-sm mb-10" data-id="element-2585">
                  לאישה • כולל סאפ, הדרכה, אמבטיית קרח ובראנץ׳
                </p>

                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 mb-8 text-right border border-rose-100" data-id="element-2586">
                  <h4 className="font-bold mb-4 text-rose-600 text-center" data-id="element-2587">
                    מה כלול במחיר:
                  </h4>
                  <ul className="space-y-3" data-id="element-2588">
                    {['הדרכת סאפ מקצועית למתחילות', 'השכרת ציוד מלא (סאפ, משוט, חגורה)', 'הדרכת נשימות ואמבטיית קרח', 'בראנץ׳ עשיר בסוף הבוקר', 'קפה ופינוקים לאורך הבוקר'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-2589">
                        <CheckCircle2 className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" data-id="element-2590" />
                        <span className="text-stone-700 font-medium" data-id="element-2591">
                          {item}
                        </span>
                      </li>)}
                  </ul>
                </div>

                <motion.button type="button" onClick={openForm} animate={{
                scale: [1, 1.03, 1]
              }} transition={{
                duration: 2.2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="group flex items-center justify-center gap-2.5 w-full bg-rose-500 hover:bg-rose-600 text-white text-xl md:text-2xl font-extrabold py-5 rounded-2xl transition-colors shadow-2xl shadow-rose-400/60 ring-4 ring-rose-200/70 focus:outline-none focus-visible:ring-4 focus-visible:ring-rose-300" data-id="element-2592">
                  <Sparkles className="w-6 h-6 shrink-0" data-id="element-2593" />
                  <span data-id="element-2594">להרשמה לבוקר הנשי</span>
                  <span className="transition-transform duration-200 group-hover:-translate-x-1" data-id="element-2595">
                    ←
                  </span>
                </motion.button>
                <p className="text-stone-500 text-sm mt-4 font-medium" data-id="element-2596">
                  השארת פרטים והמשך לתשלום מאובטח
                </p>
              </div>
            </div>
          </motion.div>

          {/* Questions / WhatsApp CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto mt-10" data-id="element-2597">
            <div className="bg-white border border-rose-100 rounded-3xl p-8 text-center shadow-sm" data-id="element-2598">
              <div className="inline-flex items-center gap-2 mb-3 text-rose-500" data-id="element-2599">
                <Heart className="w-5 h-5" data-id="element-2600" />
                <span className="text-xs font-semibold tracking-wider uppercase" data-id="element-2601">
                  אני כאן בשבילך
                </span>
                <Heart className="w-5 h-5" data-id="element-2602" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-3 leading-tight" data-id="element-2603">
                מתלבטת? יש לך עוד שאלות?
              </h3>
              <p className="text-stone-600 text-lg mb-7 leading-relaxed" data-id="element-2604">
                תכתבי לי בוואטסאפ ואשמח לדבר ולענות על כל שאלה 💌
              </p>
              <a href="https://wa.me/972548800260?text=היי%20עדי,%20יש%20לי%20כמה%20שאלות%20על%20הבוקר%20הנשי%20-%20הכוח%20שבמים" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20BA5A] text-white font-bold py-4 px-8 rounded-full transition-all shadow-lg shadow-emerald-300/40 hover:scale-105" data-id="element-2605">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" xmlns="http://www.w3.org/2000/svg" data-id="element-2606">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-2607" />
                </svg>
                תכתבי לי בוואטסאפ
              </a>
            </div>
          </motion.div>

          {/* Cancellation Policy */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto mt-10" data-id="element-2608">
            <div className="bg-white border border-rose-100 rounded-3xl p-8 shadow-sm text-right" data-id="element-2609">
              <div className="flex items-center justify-center gap-2 mb-5 text-rose-500" data-id="element-2610">
                <Info className="w-5 h-5" data-id="element-2611" />
                <span className="text-xs font-semibold tracking-wider uppercase" data-id="element-2612">
                  מדיניות ביטולים
                </span>
              </div>
              <ul className="space-y-3" data-id="element-2613">
                {[{
                when: 'ביטול עד 3 ימים לפני המועד',
                detail: 'חיוב של 25% מעלות הבוקר'
              }, {
                when: 'ביטול יום לפני המועד',
                detail: 'חיוב של 50% מעלות הבוקר'
              }, {
                when: 'ביטול ב-24 השעות שלפני האירוע',
                detail: 'ללא זיכוי'
              }].map((item, i) => <li key={i} className="flex items-start gap-3 bg-rose-50/60 border border-rose-100 rounded-2xl p-4" data-id="element-2614">
                    <Clock className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" data-id="element-2615" />
                    <div data-id="element-2616">
                      <span className="block font-bold text-stone-800" data-id="element-2617">
                        {item.when}
                      </span>
                      <span className="block text-stone-600 text-sm mt-0.5" data-id="element-2618">
                        {item.detail}
                      </span>
                    </div>
                  </li>)}
              </ul>
              <p className="text-stone-500 text-xs text-center mt-5 leading-relaxed" data-id="element-2619">
                בהרשמה ובתשלום את מאשרת את מדיניות הביטולים המפורטת מעלה.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-400 py-12 text-center border-t border-stone-800" data-id="element-2620">
        <div className="max-w-4xl mx-auto px-4" data-id="element-2621">
          <h2 className="text-2xl font-bold text-white mb-3" data-id="element-2622">
            בוקר נשי - הכוח שבמים
          </h2>
          <p className="mb-6 text-rose-200/80" data-id="element-2623">
            ארבע שעות שיחזירו אותך אל עצמך.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-8 text-sm" data-id="element-2624">
            <span className="flex items-center gap-2" data-id="element-2625">
              <Calendar size={16} data-id="element-2626" /> 31.07 · 04.09 · 08.10
            </span>
            <span className="flex items-center gap-2" data-id="element-2627">
              <Clock size={16} data-id="element-2628" /> 09:00 - 13:00
            </span>
            <span className="flex items-center gap-2" data-id="element-2629">
              <MapPin size={16} data-id="element-2630" /> חוף מעגן
            </span>
          </div>
          <p className="text-sm opacity-60" data-id="element-2631">© 2026 כל הזכויות שמורות.</p>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a href="https://wa.me/972548800260?text=היי,%20אני%20רוצה%20להירשם%20לבוקר%20הנשי%20-%20הכוח%20שבמים" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 left-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:scale-110 hover:shadow-xl transition-all duration-300 flex items-center justify-center group" aria-label="Contact on WhatsApp" data-id="element-2632">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" xmlns="http://www.w3.org/2000/svg" data-id="element-2633">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-2634" />
        </svg>
      </a>

      {/* Registration Modal */}
      <AnimatePresence data-id="element-2635">
        {isFormOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={closeForm} className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{
        backgroundColor: 'rgba(60, 20, 40, 0.55)',
        backdropFilter: 'blur(6px)'
      }} data-id="element-2636">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          type: 'spring',
          damping: 22,
          stiffness: 280
        }} onClick={e => e.stopPropagation()} dir="rtl" className="relative bg-gradient-to-br from-rose-50 via-white to-orange-50 rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-rose-200" data-id="element-2637">
              {/* Close */}
              <button type="button" onClick={closeForm} disabled={isSubmitting} className="absolute top-4 left-4 text-stone-400 hover:text-stone-700 transition-colors p-2 rounded-full hover:bg-white/70 z-10 disabled:opacity-50" aria-label="סגירה" data-id="element-2638">
                <X className="w-5 h-5" data-id="element-2639" />
              </button>

              <div className="p-7 md:p-9" data-id="element-2640">
                {/* Header */}
                <div className="text-center mb-6" data-id="element-2641">
                  <div className="inline-flex items-center gap-2 mb-3 text-rose-500" data-id="element-2642">
                    <Heart className="w-5 h-5" data-id="element-2643" />
                    <span className="text-xs font-semibold tracking-wider uppercase" data-id="element-2644">
                      הרשמה
                    </span>
                    <Heart className="w-5 h-5" data-id="element-2645" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-2" data-id="element-2646">
                    בוקר נשי - הכוח שבמים
                  </h3>
                  <p className="text-stone-600 text-sm" data-id="element-2647">
                    השאירי פרטים ונמשיך לתשלום מאובטח
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2648">
                  {/* Full name */}
                  <div data-id="element-2649">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2650">
                      שם מלא <span className="text-rose-500" data-id="element-2651">*</span>
                    </label>
                    <div className="relative" data-id="element-2652">
                      <User className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400 pointer-events-none" data-id="element-2653" />
                      <input type="text" name="fullName" value={formData.fullName} onChange={handleFormChange} required disabled={isSubmitting} placeholder="השם המלא שלך" className="w-full bg-white border border-rose-200 rounded-xl py-3 pr-10 pl-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all" data-id="element-2654" />
                    </div>
                  </div>

                  {/* Email */}
                  <div data-id="element-2655">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2656">
                      כתובת מייל <span className="text-rose-500" data-id="element-2657">*</span>
                    </label>
                    <div className="relative" data-id="element-2658">
                      <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400 pointer-events-none" data-id="element-2659" />
                      <input type="email" name="email" value={formData.email} onChange={handleFormChange} required disabled={isSubmitting} placeholder="name@example.com" dir="ltr" className="w-full bg-white border border-rose-200 rounded-xl py-3 pr-10 pl-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all text-right" data-id="element-2660" />
                    </div>
                  </div>

                  {/* Phone */}
                  <div data-id="element-2661">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2662">
                      מספר טלפון <span className="text-rose-500" data-id="element-2663">*</span>
                    </label>
                    <div className="relative" data-id="element-2664">
                      <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400 pointer-events-none" data-id="element-2665" />
                      <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} required disabled={isSubmitting} placeholder="050-1234567" dir="ltr" className="w-full bg-white border border-rose-200 rounded-xl py-3 pr-10 pl-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all text-right" data-id="element-2666" />
                    </div>
                  </div>

                  {/* Preferred event date */}
                  <div data-id="element-2667">
                    <label className="block text-sm font-semibold text-stone-700 mb-2" data-id="element-2668">
                      לאיזה תאריך תרצי להצטרף?{' '}
                      <span className="text-rose-500" data-id="element-2669">*</span>
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-2670">
                      {EVENT_DATES.map(opt => {
                    const active = formData.selectedDate === opt.value;
                    return <button key={opt.value} type="button" disabled={isSubmitting} onClick={() => setFormData(prev => ({
                      ...prev,
                      selectedDate: opt.value
                    }))} className={`flex items-center justify-center gap-2 py-3 px-2 rounded-xl font-bold text-sm border-2 transition-all ${active ? 'bg-rose-500 text-white border-rose-500 shadow-md shadow-rose-300/50' : 'bg-white text-stone-700 border-rose-200 hover:border-rose-400'}`} data-id="element-2671">
                            <Calendar className={`w-4 h-4 shrink-0 ${active ? 'text-white' : 'text-rose-400'}`} data-id="element-2672" />
                            {opt.label}
                          </button>;
                  })}
                    </div>
                  </div>

                  {/* Date of birth */}
                  <div data-id="element-2673">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2674">
                      תאריך לידה <span className="text-rose-500" data-id="element-2675">*</span>
                    </label>
                    <div className="relative" data-id="element-2676">
                      <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400 pointer-events-none" data-id="element-2677" />
                      <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleFormChange} required disabled={isSubmitting} dir="ltr" className="w-full bg-white border border-rose-200 rounded-xl py-3 pr-10 pl-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all text-right" data-id="element-2678" />
                    </div>
                  </div>

                  {/* No medical limitations checkbox */}
                  <div data-id="element-2679">
                    <label className={`flex items-start gap-3 cursor-pointer p-4 rounded-xl border-2 transition-all ${formData.noMedicalLimitations ? 'bg-rose-50 border-rose-400' : 'bg-white border-rose-200 hover:border-rose-300'} ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`} data-id="element-2680">
                      <input type="checkbox" name="noMedicalLimitations" checked={formData.noMedicalLimitations} onChange={e => setFormData(prev => ({
                    ...prev,
                    noMedicalLimitations: e.target.checked
                  }))} disabled={isSubmitting} required className="sr-only" data-id="element-2681" />
                      <div className={`shrink-0 mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${formData.noMedicalLimitations ? 'bg-rose-500 border-rose-500' : 'bg-white border-rose-300'}`} data-id="element-2682">
                        {formData.noMedicalLimitations && <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={3} data-id="element-2683" />}
                      </div>
                      <div className="flex-1" data-id="element-2684">
                        <span className="block text-sm font-semibold text-stone-800" data-id="element-2685">
                          אני מאשרת שאין לי מגבלות רפואיות{' '}
                          <span className="text-rose-500" data-id="element-2686">*</span>
                        </span>
                        <span className="block text-xs text-stone-500 mt-0.5" data-id="element-2687">
                          המאפשרות לי להשתתף בפעילות סאפ במים
                        </span>
                      </div>
                    </label>
                  </div>

                  {/* SUP experience yes/no */}
                  <div data-id="element-2688">
                    <label className="block text-sm font-semibold text-stone-700 mb-2" data-id="element-2689">
                      האם יש לך ניסיון על סאפ?{' '}
                      <span className="text-rose-500" data-id="element-2690">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-2691">
                      {[{
                    value: 'yes',
                    label: 'כן'
                  }, {
                    value: 'no',
                    label: 'לא'
                  }].map(opt => {
                    const active = formData.hasExperience === opt.value;
                    return <button key={opt.value} type="button" disabled={isSubmitting} onClick={() => setFormData(prev => ({
                      ...prev,
                      hasExperience: opt.value
                    }))} className={`py-3 rounded-xl font-bold text-sm border-2 transition-all ${active ? 'bg-rose-500 text-white border-rose-500 shadow-md shadow-rose-300/50' : 'bg-white text-stone-700 border-rose-200 hover:border-rose-400'}`} data-id="element-2692">
                            {opt.label}
                          </button>;
                  })}
                    </div>
                  </div>

                  {/* Notes / sensitivities */}
                  <div data-id="element-2693">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2694">
                      הערות / רגישויות שחשוב שנכיר
                    </label>
                    <textarea name="notes" value={formData.notes} onChange={handleFormChange} disabled={isSubmitting} rows={3} placeholder="אלרגיות, רגישויות תזונה, כל דבר שחשוב לי לדעת..." className="w-full bg-white border border-rose-200 rounded-xl py-3 px-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all resize-none" data-id="element-2695" />
                  </div>

                  {/* Submit */}
                  <button type="submit" disabled={isSubmitting} className="w-full bg-rose-500 hover:bg-rose-600 disabled:opacity-70 disabled:cursor-not-allowed text-white text-lg font-bold py-4 rounded-2xl transition-all shadow-lg shadow-rose-300/60 hover:scale-[1.01] mt-2" data-id="element-2696">
                    {isSubmitting ? 'פותחת את חלון התשלום…' : 'המשך לתשלום ←'}
                  </button>

                  <p className="text-stone-500 text-xs text-center pt-1" data-id="element-2697">
                    התשלום מאובטח ומבוצע דרך Tranzila
                  </p>

                  {/* Cancellation policy note */}
                  <div className="bg-rose-50/70 border border-rose-100 rounded-xl p-3 text-right" data-id="element-2698">
                    <div className="flex items-center gap-1.5 mb-1 text-rose-500" data-id="element-2699">
                      <Info className="w-3.5 h-3.5" data-id="element-2700" />
                      <span className="text-[11px] font-bold" data-id="element-2701">
                        מדיניות ביטולים
                      </span>
                    </div>
                    <p className="text-stone-500 text-[11px] leading-relaxed" data-id="element-2702">
                      ביטול עד 3 ימים לפני — חיוב 25% · ביטול יום לפני — חיוב
                      50% · ביטול ב-24 השעות שלפני האירוע — ללא זיכוי.
                    </p>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Payment Modal (Tranzila iframe) */}
      <AnimatePresence data-id="element-2703">
        {isPaymentOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{
        backgroundColor: 'rgba(60, 20, 40, 0.65)',
        backdropFilter: 'blur(6px)'
      }} onClick={closePayment} data-id="element-2704">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          type: 'spring',
          damping: 22,
          stiffness: 280
        }} onClick={e => e.stopPropagation()} dir="rtl" className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden border border-rose-200 flex flex-col" data-id="element-2705">
              {/* Header */}
              <div className="bg-gradient-to-l from-rose-50 to-orange-50 px-6 py-4 border-b border-rose-100 flex items-center justify-between shrink-0" data-id="element-2706">
                <div className="flex items-center gap-2 text-stone-700" data-id="element-2707">
                  {paymentSuccess ? <>
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-2708" />
                      <span className="text-sm font-semibold" data-id="element-2709">
                        ההרשמה הושלמה
                      </span>
                    </> : <>
                      <Lock className="w-4 h-4 text-rose-500" data-id="element-2710" />
                      <span className="text-sm font-semibold" data-id="element-2711">
                        תשלום מאובטח באמצעות טרנזילה
                      </span>
                    </>}
                </div>
                <div className="flex items-center gap-4" data-id="element-2712">
                  {!paymentSuccess && <div className="text-sm font-bold text-rose-600 hidden sm:block" data-id="element-2713">
                      בוקר נשי • ₪{PRICE}
                    </div>}
                  <button type="button" onClick={closePayment} className="text-stone-400 hover:text-stone-700 transition-colors p-1.5 rounded-full hover:bg-white/70" aria-label="סגירת חלון התשלום" data-id="element-2714">
                    <X className="w-5 h-5" data-id="element-2715" />
                  </button>
                </div>
              </div>

              {/* Body — either iframe or success state */}
              {paymentSuccess ? <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex-1 bg-gradient-to-br from-rose-50 via-white to-orange-50 px-8 py-12 text-center flex flex-col items-center justify-center" data-id="element-2716">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              damping: 14,
              stiffness: 200,
              delay: 0.1
            }} className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-6 ring-8 ring-emerald-50" data-id="element-2717">
                    <CheckCircle2 className="w-10 h-10 text-emerald-500" data-id="element-2718" />
                  </motion.div>
                  <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-3" data-id="element-2719">
                    ההרשמה הצליחה! 🌸
                  </h3>
                  <p className="text-stone-600 text-base md:text-lg leading-relaxed max-w-md mb-2" data-id="element-2720">
                    קיבלנו את ההרשמה ואת התשלום שלך לבוקר הנשי.
                  </p>
                  <p className="text-stone-600 text-base md:text-lg leading-relaxed max-w-md mb-8" data-id="element-2721">
                    אשלח אלייך פרטים נוספים בוואטסאפ בקרוב 💌
                  </p>

                  <div className="bg-white border border-rose-100 rounded-2xl p-5 max-w-sm w-full mb-6 text-right space-y-2" data-id="element-2722">
                    <div className="flex items-start gap-2 text-stone-700" data-id="element-2723">
                      <Calendar className="w-4 h-4 text-rose-500 mt-1 shrink-0" data-id="element-2724" />
                      <span className="font-medium" data-id="element-2725">
                        תאריכים פתוחים: שישי 31.07.2026, 04.09.2026, 08.10.2026
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-stone-700" data-id="element-2726">
                      <Clock className="w-4 h-4 text-rose-500" data-id="element-2727" />
                      <span className="font-medium" data-id="element-2728">09:00 - 13:00</span>
                    </div>
                    <div className="flex items-center gap-2 text-stone-700" data-id="element-2729">
                      <MapPin className="w-4 h-4 text-rose-500" data-id="element-2730" />
                      <span className="font-medium" data-id="element-2731">חוף מעגן, כנרת</span>
                    </div>
                  </div>

                  <button type="button" onClick={closePayment} className="bg-rose-500 hover:bg-rose-600 text-white font-bold px-8 py-3 rounded-full transition-all shadow-lg shadow-rose-300/50 hover:scale-[1.02]" data-id="element-2732">
                    סיום
                  </button>
                </motion.div> : <>
                  <div className="flex-1 bg-rose-50/50 overflow-hidden" data-id="element-2733">
                    <iframe ref={paymentIframeRef} name="tranzila" width="100%" height="600" frameBorder="0" allow="payment" className="w-full h-[600px] max-h-[78vh] bg-white" title="חלון תשלום מאובטח" data-id="element-2734"></iframe>
                  </div>
                  {/* Cancellation policy strip inside the payment window */}
                  <div className="shrink-0 bg-rose-50 border-t border-rose-100 px-5 py-3 text-right" data-id="element-2735">
                    <div className="flex items-center gap-1.5 mb-1 text-rose-500" data-id="element-2736">
                      <Info className="w-3.5 h-3.5 shrink-0" data-id="element-2737" />
                      <span className="text-[11px] font-bold" data-id="element-2738">
                        מדיניות ביטולים
                      </span>
                    </div>
                    <p className="text-stone-500 text-[11px] leading-relaxed" data-id="element-2739">
                      ביטול עד 3 ימים לפני — חיוב 25% · ביטול יום לפני — חיוב
                      50% · ביטול ב-24 השעות שלפני האירוע — ללא זיכוי.
                    </p>
                  </div>
                </>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}